import { ReactNode, useEffect, useRef } from 'react'
import { Box } from '@chakra-ui/react'
import '@/assets/css/poppins.css'
import Navbar from './navbar/navbar'
import MenuBottom from '../menus/menu-bottom'
import Footer from './footer/footer'
import RouteHeader from '@/components/elements/route-header'
import { useDispatch, useSelector } from 'react-redux'
import { AppDispatch, RootState } from '@/store/store'
import Toast from '../elements/toast'
import { fetchCategories } from '@/services/api/categories/categories'
import { setCategoriesFn } from '@/store/features/categories'
import CustomersEntity from '@/entities/api/customers/customer'
import { createCartProduct, getCartProducts } from '@/services/api/cart/cart'
import {
	addMultipleToCart,
	removeFromCart,
	clearCart,
	validateCartItems,
} from '@/store/features/cart'
import FloatingSupportChat from './parts/floating-support-chat'
import FloatingWhatsApp from './parts/floating-whatsApp'
import { clearLastRoute, getLastRoute } from '@/utils/save-lastroute'
import { useNavigate } from 'react-router-dom'
import CartEntity from '@/entities/api/cart/cart'

const Layout = ({
	children,
	customer,
	token,
}: {
	children: ReactNode
	customer: CustomersEntity | null
	token: string | null
}) => {
	const categories = useSelector(
		(state: RootState) => state.category.categories
	)
	const cart = useSelector((state: RootState) => state.cart.cart)
	const dispatch = useDispatch<AppDispatch>()
	const cartSyncedRef = useRef(false)
	const navigate = useNavigate()

	useEffect(() => {
		const getCategories = async () => {
			if (categories.length === 0) {
				try {
					const response = await fetchCategories()

					if ([404, 409, 400].includes(response?.status ?? 0)) {
						throw new Error(response?.statusText)
					}
					dispatch(setCategoriesFn(response?.data))
				} catch (error) {
					Toast({
						type: 'error',
						message: 'No se pudieron cargar las categorias',
					})
				}
			}
		}

		getCategories()
	}, [categories])

	useEffect(() => {
		if (!token || !customer) {
			cartSyncedRef.current = false
			return
		}

		if (cartSyncedRef.current) {
			return
		}

		const syncCartWithDatabase = async () => {
			try {
				const localItems = cart.filter((item : CartEntity) => item?.isLocal)
				const nonLocalItems = cart.filter((item : CartEntity) => !item?.isLocal)

				let databaseItems = []

				if (nonLocalItems.length === 0) {
					const response = await getCartProducts(customer._id!, token)

					if ([400, 401, 409, 404].includes(response.status ?? 0)) {
						if (response.status === 401) {
							Toast({
								type: 'error',
								message:
									'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
							})
						}
						cartSyncedRef.current = true
						return
					}

					databaseItems = response.data || []
				} else {
					databaseItems = nonLocalItems
				}

				if (localItems.length === 0 && databaseItems.length > 0) {
					dispatch(clearCart())
					dispatch(addMultipleToCart({ cartItems: databaseItems }))
					cartSyncedRef.current = true
					return
				}

				if (localItems.length > 0) {
					const validLocalItems = localItems.filter((item) => {
						if (
							!item.product?.attributes ||
							item.product.attributes.length === 0
						) {
							Toast({
								type: 'warning',
								message: `El producto ${
									item?.product?.name || 'desconocido'
								} no tiene atributos válidos y será removido del carrito`,
							})
							return false
						}
						return true
					})

					if (validLocalItems.length === 0 && databaseItems.length > 0) {
						dispatch(clearCart())
						dispatch(addMultipleToCart({ cartItems: databaseItems }))
						cartSyncedRef.current = true
						return
					}

					const mergeItems = async () => {
						const itemsToAdd = []
						const itemsToRemove = []
						let hasErrors = false

						for (const localItem of validLocalItems) {
							const attribute = localItem.product.attributes![0]
							const body = {
								product: localItem.product._id,
								attribute: attribute._id!,
								customer: customer._id,
								quantity: attribute.quantity!,
							}

							try {
								const response = await createCartProduct(body, token)

								switch (response.status) {
									case 200:
									case 201:
										itemsToAdd.push(response.data.data)
										itemsToRemove.push(localItem._id)
										break
									case 404:
										Toast({
											type: 'info',
											message: `El producto ${localItem?.product?.name} ya no está disponible y será removido del carrito`,
										})
										itemsToRemove.push(localItem._id)
										break
									case 409:
										const existingDbItem = databaseItems.find((dbItem: any) => {
											const dbAttr = dbItem.product.attributes?.[0]
											return (
												dbItem.product._id === localItem.product._id &&
												dbAttr?._id === attribute._id
											)
										})

										if (existingDbItem) {
											Toast({
												type: 'info',
												message: `El producto ${localItem?.product?.name} ya estaba en tu carrito. Las cantidades se han combinado.`,
											})
											itemsToRemove.push(localItem._id)
										} else {
											Toast({
												type: 'warning',
												message: `No hay suficiente stock para ${localItem?.product?.name}`,
											})
										}
										break
									case 400:
										Toast({
											type: 'error',
											message: `Error en los datos del producto ${localItem?.product?.name}`,
										})
										hasErrors = true
										break
									case 401:
										Toast({
											type: 'error',
											message:
												'Tu sesión ha expirado. Por favor, inicia sesión nuevamente.',
										})
										hasErrors = true
										break
									default:
										Toast({
											type: 'error',
											message: `Error inesperado con el producto ${localItem?.product?.name}`,
										})
										hasErrors = true
										break
								}
							} catch (error) {
								hasErrors = true
								Toast({
									type: 'error',
									message: `Error al sincronizar ${localItem?.product?.name}`,
								})
							}
						}

						itemsToRemove.forEach((itemId) => {
							if (itemId) {
								dispatch(removeFromCart({ cartId: itemId }))
							}
						})

						if (itemsToAdd.length > 0 || databaseItems.length > 0) {
							const allItems = [...itemsToAdd, ...databaseItems]
							dispatch(addMultipleToCart({ cartItems: allItems }))
						}

						dispatch(validateCartItems())

						if (hasErrors) {
							Toast({
								type: 'warning',
								message:
									'Algunos productos no se pudieron sincronizar completamente',
							})
						}
					}

					await mergeItems()
				} else if (databaseItems.length > 0) {
					dispatch(addMultipleToCart({ cartItems: databaseItems }))
				}

				cartSyncedRef.current = true
			} catch (error) {
				cartSyncedRef.current = true
				Toast({
					type: 'error',
					message: 'Hubo un error sincronizando tu carrito de compras',
				})
			}
		}

		syncCartWithDatabase()
	}, [token, customer, dispatch])

	useEffect(() => {
		if (!token || !customer) {
			cartSyncedRef.current = false
		}

		const verifyLastRoute = () => {
			const route = getLastRoute()

			if (route) {
				navigate(route.route)
				clearLastRoute()
			}
		}

		verifyLastRoute()
	}, [token, customer])

	return (
		<Box position='relative'>
			<Navbar customer={customer} token={token} />
			<RouteHeader />
			{children}
			<Footer />
			<MenuBottom />
			<FloatingWhatsApp />
			<FloatingSupportChat />
		</Box>
	)
}
export default Layout
